import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import {
  DownloadIcon,
  FileTextIcon,
  Building2,
  UploadIcon,
} from "lucide-react";
import {
  TestSessionFileKindEnum,
  type TestInstanceId,
  type TestResultImportId,
  type TestSessionFileId,
  type TestSessionId,
} from "@brewbase/db/types";
import { trpc, type RouterOutputs } from "@/hooks/trpc";
import { useTestSession } from "@/features/test-session/hooks/use-test-session";
import { ImportDialog } from "@/features/test-orders/import-dialog";
import { FieldAttachments } from "@/features/test-session/field-attachments";
import { TestSessionMatrix } from "@/features/test-session/test-session-matrix";
import { useSidebarConfig } from "@/hooks/use-sidebar-config";
import { Button } from "@/components/ui/button";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import { Input } from "@/components/ui/input";
import { TimeInput } from "@/components/ui/time-input";
import { cn } from "@/lib/utils";

/**
 * One external order.
 *
 * The results grid is the same session matrix the bench uses, deliberately:
 * an imported lab value and a hand-entered one are the same kind of record,
 * and a second grid would be a second set of rules about what a value means.
 */

export const Route = createFileRoute("/analytics/external/orders/$session_id")({
  component: OrderDetail,
  params: z.object({
    session_id: z.coerce.number().transform((v) => v as TestSessionId),
  }),
  // The import under review lives in the URL, so the review survives a reload
  // and a link to it can be shared.
  validateSearch: (search: Record<string, unknown>): { import?: number } => {
    const n = Number(search.import);
    return Number.isInteger(n) && n > 0 ? { import: n } : {};
  },
});

function OrderDetail() {
  const { session_id } = Route.useParams();
  const { import: importParam } = Route.useSearch();
  const navigate = Route.useNavigate();
  const utils = trpc.useUtils();
  const openImport = (id: TestResultImportId | null) =>
    navigate({
      search: (prev) => ({ ...prev, import: id ?? undefined }),
      replace: true,
    });

  const { data: order, isLoading } = trpc.tests.orders.get.useQuery(session_id);
  const { session, saveCell } = useTestSession(session_id);
  const { data: imports } = trpc.tests.resultImports.list.useQuery({
    sessionId: session_id,
  });
  const removeImport = trpc.tests.resultImports.delete.useMutation({
    onSuccess: () =>
      utils.tests.resultImports.list.invalidate({ sessionId: session_id }),
    onError: (error) => toast.error(error.message),
  });

  const updateOrder = trpc.tests.orders.update.useMutation({
    onSuccess: () => {
      utils.tests.orders.get.invalidate(session_id);
      utils.tests.orders.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const dispatchOrder = trpc.tests.orders.dispatch.useMutation({
    onSuccess: () => {
      toast.success("Order dispatched.");
      utils.tests.orders.get.invalidate(session_id);
      utils.tests.orders.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const removeInstance = trpc.tests.orders.removeInstance.useMutation({
    onSuccess: () => {
      utils.tests.orders.get.invalidate(session_id);
      utils.sessions.getFull.invalidate({ id: session_id });
    },
    onError: (err) => toast.error(err.message),
  });

  const partnerName = order?.external_partner?.name ?? "External order";

  useSidebarConfig({
    layer: 2,
    parent: {
      label: "External labs",
      icon: Building2,
      href: "/analytics/external",
      search: { tab: "orders" },
    },
    header: {
      title: partnerName,
      subtitle: order?.lab_order_reference ?? `Order #${session_id}`,
    },
    navigation: [],
  });

  const [labRef, setLabRef] = useState<string | null>(null);
  const [dispatchAt, setDispatchAt] = useState(() => new Date());
  const effectiveLabRef = labRef ?? order?.lab_order_reference ?? "";

  if (isLoading || !order) {
    return <div className="px-4 py-6 text-sm text-olive-9">Loading…</div>;
  }

  return (
    <div className="flex flex-col grow min-h-0 gap-4 px-4 py-4">
      <div className="grid grid-cols-3 gap-4 border border-olive-6 p-3">
        <Field label="Laboratory">
          <span className="text-sm text-olive-12">{partnerName}</span>
        </Field>
        <Field label="Dispatch">
          {order.sent_at ? (
            <span className="font-mono text-sm text-olive-12">
              {new Date(order.sent_at).toLocaleString()}
            </span>
          ) : (
            <div className="flex flex-col gap-2">
              <TimeInput
                size="sm"
                value={dispatchAt}
                onChange={setDispatchAt}
              />
              <div className="text-xs text-olive-9">
                Tube references:{" "}
                {order.instances
                  .map((instance) => instance.derived_external_sample_reference)
                  .join(", ")}
              </div>
              <Button
                size="sm"
                disabled={dispatchOrder.isPending}
                onClick={() =>
                  dispatchOrder.mutate({
                    sessionId: session_id,
                    sentAt: dispatchAt,
                    labOrderReference: effectiveLabRef.trim() || null,
                  })
                }
              >
                Dispatch order
              </Button>
            </div>
          )}
        </Field>
        <Field label="Lab order reference">
          <Input
            className="h-8 font-mono"
            placeholder="e.g. AR-26-HE-227988"
            value={effectiveLabRef}
            onChange={(e) => setLabRef(e.target.value)}
            onBlur={() => {
              if (
                labRef != null &&
                labRef !== (order.lab_order_reference ?? "")
              ) {
                updateOrder.mutate({
                  sessionId: session_id,
                  lab_order_reference: labRef.trim() || null,
                });
              }
            }}
          />
        </Field>
      </div>

      <ImportDialog
        importId={(importParam as TestResultImportId | undefined) ?? null}
        onClose={() => {
          void openImport(null);
          utils.tests.resultImports.list.invalidate({ sessionId: session_id });
        }}
        onCommitted={() => {
          void openImport(null);
          utils.tests.orders.get.invalidate(session_id);
          utils.sessions.getFull.invalidate({ id: session_id });
        }}
      />

      <FilesSection
        sessionId={session_id}
        files={order.files}
        partnerId={order.external_test_partner_id}
        onChanged={() => utils.tests.orders.get.invalidate(session_id)}
        onImportStarted={(id) => void openImport(id)}
      />

      {(imports ?? []).length > 0 && (
        <div className="border border-olive-6">
          <SectionHeader>Imports</SectionHeader>
          <div className="flex items-center gap-2 border-b border-olive-6 px-3 py-1 text-xs text-olive-11">
            <span className="size-3.5" />
            <span className="flex-1">File</span>
            <span className="w-24">Status</span>
            <span className="w-32" />
          </div>
          <div className="divide-y divide-olive-4">
            {(imports ?? []).map((imp) => (
              <div
                key={imp.id}
                className="flex items-center gap-2 px-3 py-1.5 text-sm"
              >
                <FileTextIcon className="size-3.5 text-olive-9" />
                <span className="flex-1 truncate">
                  {imp.filename ?? `Import #${imp.id}`}
                </span>
                <span
                  className={cn(
                    "w-24 font-mono text-xs",
                    imp.status === "FAILED" ? "text-red-11" : "text-olive-9",
                  )}
                  title={imp.error ?? undefined}
                >
                  {imp.status.toLowerCase()}
                </span>
                <div className="flex w-32 items-center justify-end gap-1">
                  {imp.status !== "COMMITTED" && (
                    <>
                      <Button
                        size="sm"
                        onClick={() =>
                          void openImport(imp.id as TestResultImportId)
                        }
                      >
                        Review
                      </Button>
                      <DeleteConfirmDialog
                        title="Discard this import?"
                        description={`"${imp.filename ?? `Import #${imp.id}`}" and everything reviewed on it will be removed. The uploaded file is kept.`}
                        deleteLabel="Discard"
                        isDeleting={removeImport.isPending}
                        onConfirm={() => removeImport.mutate(imp.id)}
                      />
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bounded so the matrix scrolls itself and its header stays in view */}
      <div className="border border-olive-6 flex flex-col max-h-[80vh]">
        <SectionHeader>Results</SectionHeader>
        {session ? (
          <TestSessionMatrix
            className="min-h-0"
            session={session}
            saveCell={saveCell}
            onRemoveInstance={(id: TestInstanceId) => removeInstance.mutate(id)}
          />
        ) : (
          <div className="px-3 py-4 text-sm text-olive-9">Loading results…</div>
        )}
      </div>
      {session && <FieldAttachments session={session} />}
    </div>
  );
}

/**
 * Report files. Uploading here attaches the file and starts an import from it —
 * attaching a report without reading it in is the rarer case, and the review
 * screen can always be abandoned.
 */
function FilesSection({
  sessionId,
  files,
  partnerId,
  onChanged,
  onImportStarted,
}: {
  sessionId: TestSessionId;
  files: NonNullable<RouterOutputs["tests"]["orders"]["get"]>["files"];
  partnerId: number | null;
  onChanged: () => void;
  onImportStarted: (id: TestResultImportId) => void;
}) {
  const utils = trpc.useUtils();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const removeFile = trpc.tests.orders.removeFile.useMutation({
    onSuccess: onChanged,
    onError: (err) => toast.error(err.message),
  });

  const upload = async (file: File) => {
    if (partnerId == null) {
      toast.error("This order has no lab, so results cannot be read in.");
      return;
    }
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("partner_id", String(partnerId));
      body.append("session_id", String(sessionId));
      const res = await fetch("/api/tests/result-imports", {
        method: "POST",
        body,
        credentials: "include",
      });
      const payload = (await res.json()) as { id?: number; error?: string };
      if (!res.ok || !payload.id)
        throw new Error(payload.error ?? "Upload failed");
      onChanged();
      utils.tests.resultImports.list.invalidate({ sessionId });
      // A multi-sheet workbook opens on the sheet picker; the review asks.
      onImportStarted(payload.id as TestResultImportId);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const openFile = async (fileId: TestSessionFileId) => {
    const url = await utils.tests.orders.getFileUrl.fetch(fileId);
    window.open(url, "_blank", "noopener");
  };

  const kindLabel = useMemo(
    () => (kind: TestSessionFileKindEnum) =>
      kind === TestSessionFileKindEnum.REPORT ? "report" : "raw export",
    [],
  );

  return (
    <div className="border border-olive-6">
      <SectionHeader>
        <span className="flex-1">Reports</span>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept=".pdf,.csv,.xlsx,.xls"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (file) void upload(file);
            e.target.value = "";
          }}
        />
        <Button
          size="sm"
          variant="ghost"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          <UploadIcon className="size-3.5 mr-1.5" />
          {uploading ? "Uploading…" : "Upload results"}
        </Button>
      </SectionHeader>
      {files.length === 0 ? (
        <div className="px-3 py-3 text-sm text-olive-9">
          No reports yet. Upload a lab PDF or a portal export and the values are
          read in from it.
        </div>
      ) : (
        <div className="divide-y divide-olive-4">
          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center gap-2 px-3 py-1.5 text-sm"
            >
              <FileTextIcon className="size-3.5 text-olive-9" />
              <button
                type="button"
                className="flex-1 truncate text-left hover:underline"
                onClick={() => openFile(file.id as TestSessionFileId)}
              >
                {file.filename}
              </button>
              <span className="text-xs text-olive-9">
                {kindLabel(file.kind)}
              </span>
              {file.report_version && (
                <span className="font-mono text-xs text-olive-11">
                  v{file.report_version}
                </span>
              )}
              <Button
                size="icon"
                variant="ghost"
                title="Open"
                onClick={() => openFile(file.id as TestSessionFileId)}
              >
                <DownloadIcon className="size-3.5" />
              </Button>
              <DeleteConfirmDialog
                title="Remove file?"
                description={`${file.filename} is removed from this order. Values already imported from it stay, and the order's activity keeps a record of it.`}
                deleteLabel="Remove"
                onConfirm={() => removeFile.mutate(file.id as TestSessionFileId)}
                isDeleting={removeFile.isPending}
                trigger={
                  <Button size="sm" variant="ghost">
                    Remove
                  </Button>
                }
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 h-8 px-3 bg-olive-2 border-b border-olive-6 text-sm font-medium">
      {children}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="pb-1 text-xs font-medium text-olive-11">{label}</div>
      {children}
    </div>
  );
}
